import type { OrderCancellationCode } from "@loot-os/core/db/schema";
import {
  isTargetRefusedForType,
  ORDER_TRANSITIONS,
} from "@loot-os/fulfillment-core/order/state-machine";
import {
  MAX_CANCELLATION_REASON,
  type OrderStatus,
  type OrderType,
} from "@loot-os/fulfillment-core/order/types";

/**
 * Statuses the transition buttons offer. CANCELLED is excluded because the
 * transition procedure refuses it; cancelling goes through orders.cancel. So
 * is a target the order's type refuses, such as FAIL on a WITHDRAW_ASSET order.
 */
export function transitionTargets(
  status: OrderStatus,
  type: OrderType,
): Exclude<OrderStatus, "CANCELLED">[] {
  return ORDER_TRANSITIONS[status].filter(
    (target): target is Exclude<OrderStatus, "CANCELLED"> =>
      target !== "CANCELLED" && !isTargetRefusedForType(type, target),
  );
}

/** The causes a fulfiller picks from. TENANT_CANCELLED is written by the tenant API alone. */
export const FULFILLER_CANCELLATION_CODES = [
  "PRICE_EXCEEDED",
  "OUT_OF_STOCK",
] as const satisfies readonly `${OrderCancellationCode}`[];

export type FulfillerCancellationCode = (typeof FULFILLER_CANCELLATION_CODES)[number];

/** What the cancel form says about a note the mutation would refuse, or undefined when it holds. */
export function cancellationReasonError(reason: string): string | undefined {
  const trimmed = reason.trim();
  if (!trimmed) return "Say why you are cancelling.";
  if (trimmed.length > MAX_CANCELLATION_REASON) {
    return `Keep the note to ${MAX_CANCELLATION_REASON} characters.`;
  }
  return undefined;
}
